import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Utilities for exporting OfferPage and ComparisonPage to PDF
 */

/**
 * Get optimal PDF format based on content type and size
 * @param {string} pageType - 'offer' or 'comparison'
 * @param {number} offersCount - Number of offers (for comparison page)
 * @returns {object} PDF configuration
 */
export const getOptimalPDFFormat = (pageType, offersCount = 1) => {
  if (pageType === 'offer') {
    // Single offer - A4 landscape for better two-column layout
    return {
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    };
  } else if (pageType === 'comparison') {
    if (offersCount <= 2) {
      // 1-2 offers - A4 landscape should fit
      return {
        orientation: 'landscape',
        unit: 'mm', 
        format: 'a4'
      };
    } else if (offersCount <= 4) {
      // 3-4 offers - A3 landscape
      return {
        orientation: 'landscape',
        unit: 'mm',
        format: 'a3'
      };
    } else {
      // 5+ offers - custom large format
      return {
        orientation: 'landscape',
        unit: 'px',
        format: [1600, 1000] // Custom size for wide content
      };
    }
  }
  
  // Default fallback
  return {
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  };
};

/**
 * Export container element to PDF
 * @param {string} containerId - ID of the container element to export
 * @param {string} filename - Output filename (without extension)
 * @param {string} pageType - 'offer' or 'comparison' 
 * @param {number} offersCount - Number of offers (for comparison page)
 */
export const exportToPDF = async (containerId, filename, pageType = 'offer', offersCount = 1) => {
  try {
    // Get the container element
    const element = document.getElementById(containerId);
    if (!element) {
      throw new Error(`Element with id "${containerId}" not found`);
    }

    // Show loading indicator if needed
    const loadingIndicator = showLoadingIndicator();

    // Get optimal PDF format
    const pdfConfig = getOptimalPDFFormat(pageType, offersCount);

    // Temporarily hide elements with no-print class
    const noPrintElements = element.querySelectorAll('.no-print');
    noPrintElements.forEach(el => {
      el.style.display = 'none';
    });

    // Show PDF-only elements (like Ringostat header, PDF grid)
    const pdfHeaderElements = element.querySelectorAll('.pdf-header');
    pdfHeaderElements.forEach(el => {
      el.style.display = 'flex';
    });
    const pdfOnlyOffers = element.querySelectorAll('.pdf-only-offers');
    pdfOnlyOffers.forEach(el => {
      el.style.display = 'block';
    });

    // Fix gradient text (background-clip: text not supported by html2canvas)
    const gradientTexts = [];
    element.querySelectorAll('*').forEach(el => {
      const style = getComputedStyle(el);
      if (style.webkitBackgroundClip === 'text' || style.backgroundClip === 'text') {
        gradientTexts.push({
          el,
          background: el.style.background,
          webkitBackgroundClip: el.style.webkitBackgroundClip,
          webkitTextFillColor: el.style.webkitTextFillColor,
          backgroundClip: el.style.backgroundClip,
          color: el.style.color,
        });
        el.style.background = 'none';
        el.style.webkitBackgroundClip = 'unset';
        el.style.webkitTextFillColor = 'unset';
        el.style.backgroundClip = 'unset';
        el.style.color = '#006e2a';
      }
    });

    // Generate canvas from element
    const canvas = await html2canvas(element, {
      scale: 2, // High DPI for better quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#f8f9fa', // Match app background
      width: element.scrollWidth,
      height: element.scrollHeight,
      scrollX: 0,
      scrollY: -window.scrollY
    });

    // Hide PDF-only offers grid back
    pdfOnlyOffers.forEach(el => {
      el.style.display = 'none';
    });

    // Restore gradient text
    gradientTexts.forEach(({ el, background, webkitBackgroundClip, webkitTextFillColor, backgroundClip, color }) => {
      el.style.background = background;
      el.style.webkitBackgroundClip = webkitBackgroundClip;
      el.style.webkitTextFillColor = webkitTextFillColor;
      el.style.backgroundClip = backgroundClip;
      el.style.color = color;
    });

    // Restore visibility of no-print elements
    noPrintElements.forEach(el => {
      el.style.display = '';
    });

    // Hide PDF-only elements back
    pdfHeaderElements.forEach(el => {
      el.style.display = 'none';
    });

    // Single-page PDF: custom page size matches content aspect ratio
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Use A4 width as base (297mm for comparison to fit side-by-side cards)
    const pdfWidthMM = pageType === 'comparison' && offersCount >= 2 ? 297 : 210;
    const pdfHeightMM = (canvasHeight / canvasWidth) * pdfWidthMM;

    // jsPDF format: [width, height] in mm, portrait orientation
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [pdfWidthMM, pdfHeightMM],
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidthMM, pdfHeightMM);

    // Add metadata
    pdf.setProperties({
      title: filename,
      subject: `Ringostat ${pageType === 'offer' ? 'Offer' : 'Comparison'}`,
      author: 'Ringostat',
      creator: 'Ringostat Pricing Calculator'
    });

    // Hide loading indicator
    hideLoadingIndicator(loadingIndicator);

    // Download the PDF
    pdf.save(`${filename}.pdf`);

    return true;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    
    // Hide loading indicator on error
    hideLoadingIndicator();
    
    // Show user-friendly error
    alert(`Error generating PDF: ${error.message}`);
    return false;
  }
};

/**
 * Show loading indicator during PDF generation
 */
const showLoadingIndicator = () => {
  const indicator = document.createElement('div');
  indicator.id = 'pdf-loading-indicator';
  indicator.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 20px 40px;
    border-radius: 8px;
    z-index: 10000;
    font-family: Arial, sans-serif;
    font-size: 16px;
    text-align: center;
  `;
  indicator.innerHTML = `
    <div style="margin-bottom: 10px;">📄 Generating PDF...</div>
    <div style="font-size: 14px; opacity: 0.8;">This may take a few seconds</div>
  `;
  document.body.appendChild(indicator);
  return indicator;
};

/**
 * Hide loading indicator
 */
const hideLoadingIndicator = (indicator) => {
  if (indicator && indicator.parentNode) {
    indicator.parentNode.removeChild(indicator);
  } else {
    const existingIndicator = document.getElementById('pdf-loading-indicator');
    if (existingIndicator && existingIndicator.parentNode) {
      existingIndicator.parentNode.removeChild(existingIndicator);
    }
  }
};

/**
 * Generate filename for exported PDF
 * @param {string} pageType - 'offer' or 'comparison'
 * @param {string} offerName - Name of the offer/tab
 * @param {string} clientName - Client name from metadata
 * @param {string} validDate - Valid until date (DD.MM.YY format)
 * @param {boolean} isComparison - Whether offer is part of a comparison
 */
export const generatePDFFilename = (pageType, offerName = '', clientName = '', validDate = '', isComparison = false) => {
  // Helper function to sanitize filename parts
  const sanitize = (str) => str.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_').slice(0, 30);
  
  const safeClientName = sanitize(clientName) || 'Client';
  const safeValidDate = validDate.replace(/\./g, '_') || ''; // Replace dots with underscores (dd_mm_yy)
  
  if (pageType === 'offer') {
    if (isComparison && offerName) {
      // Comparison offer with tab name: Clientname_Ringostat_Offername_Validtodate
      const safeOfferName = sanitize(offerName);
      return `${safeClientName}_Ringostat_${safeOfferName}_${safeValidDate}`;
    } else {
      // Standalone offer: Clientname_Ringostat_offer_Validtodate
      return `${safeClientName}_Ringostat_offer_${safeValidDate}`;
    }
  } else if (pageType === 'comparison') {
    // Comparison: Clientname_Ringostat_Comparison_Validtodate
    return `${safeClientName}_Ringostat_Comparison_${safeValidDate}`;
  }

  // Fallback
  return `${safeClientName}_Ringostat_${pageType}_${safeValidDate}`;
};